import { User } from '../types';
import { clearCachedUsers } from './dataCache';

// API Service for PHP backend (cPanel/shared hosting friendly)
// Use VITE_API_URL when provided, otherwise default to same-origin /api
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class ApiRequestError extends Error {
    constructor(message: string, readonly status: number) {
        super(message);
        this.name = 'ApiRequestError';
    }
}

interface ApiResponse<T> {
    data?: T;
    error?: string;
    user?: any;
    success?: boolean;
    message?: string;
}

class ApiService {
    private baseUrl: string;
    private csrfToken: string | null = null;
    private csrfTokenPromise: Promise<void> | null = null;
    private csrfRequestId = 0;

    constructor() {
        this.baseUrl = API_BASE_URL;
    }

    private syncCsrfTokenFromPayload(payload: unknown): void {
        if (
            payload &&
            typeof payload === 'object' &&
            'csrf_token' in payload &&
            typeof (payload as { csrf_token?: unknown }).csrf_token === 'string' &&
            (payload as { csrf_token: string }).csrf_token.length > 0
        ) {
            this.csrfRequestId += 1;
            this.csrfToken = (payload as { csrf_token: string }).csrf_token;
        }
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {},
        retryCount: number = 0
    ): Promise<ApiResponse<T>> {
        const url = `${this.baseUrl}${endpoint}`;

        const method = (options.method || 'GET').toUpperCase();
        if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && endpoint !== '/auth/csrf') {
            await this.ensureCsrfToken();
        }

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...options.headers,
        };
        if (this.csrfToken && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
            (headers as Record<string, string>)['X-CSRF-Token'] = this.csrfToken;
        }

        try {
            const response = await fetch(url, {
                ...options,
                headers,
                credentials: 'include',
            });

            if (response.status === 429) {
                throw new ApiRequestError(
                    'Too many requests. Please try again later.',
                    response.status,
                );
            }

            if (response.status === 419 && retryCount === 0) {
                await this.ensureCsrfToken(true);
                return this.request<T>(endpoint, options, retryCount + 1);
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                this.syncCsrfTokenFromPayload(errorData);
                throw new ApiRequestError(
                    errorData.message || errorData.error || `HTTP ${response.status}`,
                    response.status,
                );
            }

            const data = await response.json();
            this.syncCsrfTokenFromPayload(data);
            return { data };

        } catch (error) {
            if (!(error instanceof ApiRequestError) && retryCount < 2) {
                const backoffMs = Math.min(1000 * Math.pow(2, retryCount), 4000);
                await new Promise((r) => setTimeout(r, backoffMs));
                return this.request<T>(endpoint, options, retryCount + 1);
            }
            
            console.error(`API request failed for ${endpoint}:`, error);
            throw error; 
        }
    }

    private async ensureCsrfToken(forceRefresh: boolean = false): Promise<void> {
        if (forceRefresh) {
            this.csrfRequestId += 1;
            this.csrfToken = null;
            this.csrfTokenPromise = null;
        }
        if (this.csrfToken) return;
        if (this.csrfTokenPromise) {
            await this.csrfTokenPromise;
            return;
        }

        const requestId = ++this.csrfRequestId;
        const fetchPromise = (async () => {
            const response = await fetch(`${this.baseUrl}/auth/csrf`, {
                method: 'GET',
                credentials: 'include',
                cache: 'no-store',
                headers: {
                    Accept: 'application/json',
                },
            });
            if (!response.ok) throw new Error('Unable to initialize request security');
            const data = await response.json();
            if (typeof data?.csrf_token !== 'string' || !data.csrf_token) {
                throw new Error('Invalid CSRF response');
            }
            if (this.csrfRequestId === requestId) {
                this.csrfToken = data.csrf_token;
            }
        })();

        this.csrfTokenPromise = fetchPromise;
        try {
            await fetchPromise;
        } finally {
            if (this.csrfTokenPromise === fetchPromise) {
                this.csrfTokenPromise = null;
            }
        }
    }

    // Authentication methods
    async login(identifier: string, password: string): Promise<ApiResponse<any>> {
        return this.request<any>('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ identifier, password }),
        });
    }

    async getMe(): Promise<ApiResponse<any>> {
        return this.request('/auth/me');
    }

    async verifyPassword(password: string): Promise<ApiResponse<any>> {
        const response = await this.request('/auth/verify-password', {
            method: 'POST',
            body: JSON.stringify({ password }),
        });

        if (response && response.success === false) {
            throw new Error(response.message || 'Incorrect password');
        }

        return response;
    }

    async requestPasswordReset(email: string): Promise<ApiResponse<any>> {
        return this.request('/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    }

    async resetPassword(token: string, password: string): Promise<ApiResponse<any>> {
        return this.request('/auth/reset-password', {
            method: 'POST',
            body: JSON.stringify({ token, password }),
        });
    }

    async resetPasswordStatus(token: string): Promise<ApiResponse<any>> {
        return this.request('/auth/reset-password/status', {
            method: 'POST',
            body: JSON.stringify({ token }),
        });
    }

    async createPaymentCheckout(projectId: string): Promise<ApiResponse<{ checkoutUrl: string }>> {
        return this.request('/payments/checkout', {
            method: 'POST',
            body: JSON.stringify({ projectId }),
        });
    }

    async verifyPaymentCheckout(projectId: string): Promise<ApiResponse<{ documentsLocked: boolean; balanceDue: number }>> {
        return this.request('/payments/verify', {
            method: 'POST',
            body: JSON.stringify({ projectId }),
        });
    }

    async changePassword(newPassword: string, currentPassword: string = ''): Promise<ApiResponse<any>> {
        return this.request('/auth/change-password', {
            method: 'POST',
            body: JSON.stringify({ currentPassword, newPassword }),
        });
    }

    async logout(): Promise<void> {
        // Clear legacy browser state immediately; server logout may complete later.
        localStorage.removeItem('authToken');
        try {
            await this.request('/auth/logout', { method: 'POST' });
        } catch (error) {
            console.warn('Server logout failed; local session state was cleared.', error);
        } finally {
            this.csrfRequestId += 1;
            this.csrfToken = null;
            this.csrfTokenPromise = null;
            localStorage.removeItem('authToken');
        }
    }

    // Project methods
    async getProjects(): Promise<ApiResponse<any[]>> {
        return this.request('/projects');
    }

    async createProject(projectData: any): Promise<ApiResponse<any>> {
        return this.request('/projects', {
            method: 'POST',
            body: JSON.stringify(projectData),
        });
    }

    async updateProject(projectId: string, updates: any): Promise<ApiResponse<any>> {
        return this.request(`/projects/${projectId}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
    }

    async archiveProject(
        projectId: string,
        archive: { outcome: 'completed' | 'cancelled'; reason?: string; notes?: string }
    ): Promise<ApiResponse<any>> {
        return this.request(`/projects/${projectId}/archive`, {
            method: 'PUT',
            body: JSON.stringify(archive),
        });
    }

    async restoreProject(projectId: string): Promise<ApiResponse<any>> {
        return this.request(`/projects/${projectId}/archive`, {
            method: 'DELETE',
        });
    }

    async deleteProject(projectId: string): Promise<ApiResponse<any>> {
        return this.request(`/projects/${projectId}`, {
            method: 'DELETE',
        });
    }

    async respondToAssignment(
        projectId: string,
        response: 'accepted' | 'declined',
        assignmentId?: string
    ): Promise<ApiResponse<any>> {
        const payload: Record<string, string> = { projectId, response };
        if (assignmentId) {
            payload.assignmentId = assignmentId;
        }
        return this.request('/projects/respond-assignment', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    // Task methods
    async getTasks(): Promise<ApiResponse<any[]>> {
        return this.request('/tasks');
    }

    // Explicitly typed to handle array of assignedTo
    async createTask(taskData: {
        title: string;
        description: string;
        status: string;
        priority: string;
        projectId: string;
        assignedTo: string[]; // Support multi-assignment
        dueDate: string;
        createdBy: string;
    }): Promise<ApiResponse<any>> {
        return this.request('/tasks', {
            method: 'POST',
            body: JSON.stringify(taskData),
        });
    }

    async updateTask(taskId: string, updates: any): Promise<ApiResponse<any>> {
        return this.request(`/tasks/${taskId}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
    }

    async deleteTask(taskId: string): Promise<ApiResponse<any>> {
        return this.request(`/tasks/${taskId}`, {
            method: 'DELETE',
        });
    }

    // Work log methods
    async getWorkLogs(): Promise<ApiResponse<any[]>> {
        return this.request('/worklogs');
    }

    async createWorkLog(workLogData: any): Promise<ApiResponse<any>> {
        return this.request('/worklogs', {
            method: 'POST',
            body: JSON.stringify(workLogData),
        });
    }

    async updateWorkLog(workLogId: string, updates: any): Promise<ApiResponse<any>> {
        return this.request(`/worklogs/${workLogId}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
    }

    async deleteWorkLog(workLogId: string): Promise<ApiResponse<any>> {
        return this.request(`/worklogs/${workLogId}`, {
            method: 'DELETE',
        });
    }

    // Materials methods
    async getMaterials(): Promise<ApiResponse<any[]>> {
        return this.request('/materials');
    }

    async createMaterial(materialData: any): Promise<ApiResponse<any>> {
        return this.request('/materials', {
            method: 'POST',
            body: JSON.stringify(materialData),
        });
    }
    async updateMaterial(id: string, materialData: any): Promise<ApiResponse<any>> {
        return this.request(`/materials/${id}`, {
            method: 'PUT',
            body: JSON.stringify(materialData),
        });
    }

    async deleteMaterial(id: string): Promise<ApiResponse<any>> {
        return this.request(`/materials/${id}`, {
            method: 'DELETE',
        });
    }

    // Users methods
    async getUsers(params?: Record<string, any>): Promise<ApiResponse<User[]>> {
        const query = params ? `?${new URLSearchParams(params).toString()}` : "";
        return this.request(`/users${query}`, { method: "GET" });
    }

    async getInactiveUsers(): Promise<ApiResponse<User[]>> {
        return this.request("/users/inactive", { method: "GET" });
    }

    async createUser(data: {
        name: string;
        email: string;
        password: string;
        phone?: string;
        school?: string;
        roles: User["roles"];
    }): Promise<ApiResponse<{ user: User; message: string }>> {
        const response = await this.request<{ user: User; message: string }>('/users', {
            method: 'POST',
            body: JSON.stringify(data),
        });
        clearCachedUsers();
        return response;
    }

    async updateUser(id: string, data: Partial<User>): Promise<ApiResponse<any>> {
        const response = await this.request(`/users/${id}`, {
            method: "PUT",
            body: JSON.stringify(data),
        });
        clearCachedUsers();
        return response;
    }

    async changeUserPassword(id: string, password: string): Promise<ApiResponse<any>> {
        return this.request(`/users/${id}/password`, {
            method: "PUT",
            body: JSON.stringify({ password }),
        });
    }

    async makeUserInactive(userId: string): Promise<ApiResponse<any>> {
        const response = await this.request(`/users/inactive/${userId}`, {
            method: "PUT",
            body: JSON.stringify({ is_active: 0 }),
        });
        clearCachedUsers();
        return response;
    }

    async makeUserActive(userId: string): Promise<ApiResponse<any>> {
        const response = await this.request(`/users/active/${userId}`, {
            method: "PUT",
            body: JSON.stringify({ is_active: 1 }),
        });
        clearCachedUsers();
        return response;
    }

    async uploadGcashQr(file: File): Promise<ApiResponse<{ gcash_qr_url: string }>> {
        // Gamitin natin ang raw fetch kasi kailangan natin ng FormData (hindi JSON)
        try {
            await this.ensureCsrfToken();

            const performUpload = () => {
                const formData = new FormData();
                formData.append('qr_image', file);

                return fetch(`${this.baseUrl}/users/upload-gcash-qr`, {
                    method: 'POST',
                    body: formData,
                    headers: this.csrfToken ? { 'X-CSRF-Token': this.csrfToken } : {},
                    credentials: 'include', // Important para sa session authentication
                });
            };

            let response = await performUpload();

            if (response.status === 419) {
                await this.ensureCsrfToken(true);
                response = await performUpload();
            }

            const result = await response.json().catch(() => ({}));
            this.syncCsrfTokenFromPayload(result);

            if (!response.ok || result.error) {
                throw new Error(result.error || result.message || 'Upload failed');
            }

            return {
                data: result,
                success: true,
            };
        } catch (error: unknown) {
            console.error('GCash QR upload failed:', error);
            const message = error instanceof Error ? error.message : 'Failed to upload QR code';
            return {
                error: message || 'Failed to upload QR code',
                success: false,
            };
        }
    }

    async createClient(clientData: any): Promise<ApiResponse<any>> {
        return this.request('/users/client', {
            method: 'POST',
            body: JSON.stringify(clientData),
        });
    }

    // Health check
    async healthCheck(): Promise<ApiResponse<any>> {
        return this.request('/health');
    }

    // Reports methods
    async getReports(): Promise<ApiResponse<any[]>> {
        return this.request('/reports', {
            method: 'GET',
            credentials: 'include', 
        });
    }

    async createReport(reportData: any): Promise<ApiResponse<any>> {
        return this.request('/reports', {
            method: 'POST',
            body: JSON.stringify(reportData),
            credentials: 'include',
        });
    }

    async editReport(reportId: string, updates: any): Promise<ApiResponse<any>> {
        return this.request(`/reports/${reportId}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
    }
    async deleteReport(reportId: string): Promise<ApiResponse<any>> {
        return this.request(`/reports/${reportId}`, {
            method: 'DELETE',
        });
    }
    
    async getLogs(): Promise<ApiResponse<any[]>> {
        return this.request('/activity-logs');
    }

    async exportReport(reportId: string, supervisorId: string): Promise<ApiResponse<any>> {
        return this.request(`/reports/${reportId}/export`, {
            method: 'POST',
            body: JSON.stringify({ supervisorId }),
            credentials: 'include',
        });
    }

    // Announcements
    async getAnnouncements(): Promise<ApiResponse<any[]>> {
        return this.request('/announcements');
    }

    async createAnnouncement(data: { title: string; content: string; targetRoles: string[] }): Promise<ApiResponse<any>> {
        return this.request('/announcements', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateAnnouncement(id: number, data: { title?: string; content?: string; targetRoles?: string[] }): Promise<ApiResponse<any>> {
        return this.request(`/announcements/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteAnnouncement(id: number): Promise<ApiResponse<any>> {
        return this.request(`/announcements/${id}`, {
            method: 'DELETE',
        });
    }

    async broadcastToFabricators(projectId: string, message: string = ""): Promise<ApiResponse<any>> {
        return this.request('/projects/broadcast-fabricators', {
            method: 'POST',
            body: JSON.stringify({ projectId, message }),
        });
    }

    async getReportAnalytics(reportId: string): Promise<ApiResponse<any>> {
        return this.request(`/reports/${reportId}/analytics`, {
            method: 'GET',
            credentials: 'include',
        });
    }

    async getBillingSummary(): Promise<ApiResponse<any[]>> {
        return this.request('/billing/summary');
    }

    async createPayment(paymentData: any): Promise<ApiResponse<any>> {
        return this.request('/billing/payment', {
            method: 'POST',
            body: JSON.stringify(paymentData),
        });
    }

    async deletePayment(id: number): Promise<ApiResponse<any>> {
        return this.request(`/billing/payment/${id}`, {
            method: 'DELETE',
        });
    }

    // Feature Bank R&D API methods
    async getFeatureBankItems(): Promise<ApiResponse<any[]>> {
        return this.request('/feature-bank');
    }

    async createFeatureBankItem(itemData: {
        title: string;
        description: string;
        category?: string;
        complexity?: string;
        status?: string;
    }): Promise<ApiResponse<any>> {
        return this.request('/feature-bank', {
            method: 'POST',
            body: JSON.stringify(itemData),
        });
    }

    async upvoteFeatureBankItem(id: number | string): Promise<ApiResponse<any>> {
        return this.request(`/feature-bank/${id}/upvote`, {
            method: 'POST',
        });
    }
}

export const apiService = new ApiService();
